import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { Label } from 'web-ui-components/lib/atoms/forms';
import SourceAccount from '../../../../sharedComponents/ToAccount/ToAccount';
import { payeePropTypes } from '../../../../sharedComponents/PayeeList/propTypes';
import { bacsReferenceMaxLength, bacsDebitTransactionCodeOptions, bacsReferenceMinLength } from '../../../../config';
import {
	removePayment,
	updateAmount,
	updateReference,
	updateTransactionCode,
} from '../../../../states/bacsDebit/actions';
import { getPaymentById } from '../../../../states/bacsDebit/selectors';
import { initValidation, updateValidation, hideErrorMsg } from '../../../../states/validation/actions';
import { getValidationRowState } from '../../../../states/validation/selectors';

import styles from '../BacsPaymentStyles';
import { toAccountPropType } from '../.././../../states/accounts/propTypes';
import { BacsDebitRef as Reference } from '../Form/Reference';
import TransactionCode from '../Form/TransactionCode';
import PaymentRow from './PaymentRow';

const mapState = (state, props) => ({
	payment: getPaymentById(state, props.id),
	validations: getValidationRowState(state, props.id),
});

const mapDispatch = (dispatch, props) => ({
	handleRemovePayment: () =>
		dispatch(
			removePayment({
				id: props.id,
			})
		),
	handleAmountBlur: (value = '') => {
		if (props.amount !== value) {
			dispatch(
				updateAmount({
					id: props.id,
					amount: value,
				})
			);
		}
	},
	handleReferenceChange: (value) => {
		dispatch(
			updateReference({
				id: props.id,
				reference: value,
			})
		);
	},
	handleSelectTransactionCode: (event) => {
		dispatch(
			updateTransactionCode({
				id: props.id,
				transactionCode: event.target.value,
			})
		);
	},
	initValidation: () =>
		dispatch(
			initValidation({
				id: props.id,
			})
		),
	updateValidation: (key, isValid) =>
		dispatch(
			updateValidation({
				id: props.id,
				key,
				isValid,
			})
		),
	hideErrorMsg: (key) =>
		dispatch(
			hideErrorMsg({
				id: props.id,
				key,
			})
		),
});
export default ((WrappedComponent) => {
	const DebitPaymentRow = (props) => {
		const renderAccountField = (props) => {
			const {
				id,
				destinationAccount,
				sourcePayees,
				payment,
				content,
				handleSourceCardClick,
				showValidation,
				validations,
				hideErrorMsg,
				updateValidation,
				sourceAccounts,
			} = props;
			const { fromAccount: sourceAccount } = payment;
			const { toLabel, fromLabel } = content;
			return (
				<span>
					<Label className={classnames(styles.main__inputLabel)}>{toLabel}</Label>
					<SourceAccount
						id={id}
						htmlFor={`field-to-account-${id}`}
						fromAccount={destinationAccount}
						toPayees={sourcePayees}
						toAccounts={sourceAccounts}
						toAccount={sourceAccount}
						handleCardClick={handleSourceCardClick}
						showValidation={showValidation}
						validations={validations}
						updateValidation={updateValidation}
						hideErrorMsg={hideErrorMsg}
						title={fromLabel}
						disable={!destinationAccount}
					/>
				</span>
			);
		};
		const renderHMRCRefOrTransactionCodeField = (props) => {
			const {
				id,
				payment,
				content,
				showValidation,
				validations,
				hideErrorMsg,
				updateValidation,
				handleSelectTransactionCode,
				templateMode,
			} = props;
			const { transactionCode } = payment;
			const { transactionCodeLabel, transactionCodeInvalid } = content;
			return (
				<span>
					<Label className={classnames(styles.main__inputLabel)}>{transactionCodeLabel}</Label>
					<TransactionCode
						id={`field-transaction-code--${id}`}
						options={bacsDebitTransactionCodeOptions}
						transactionCode={transactionCode}
						showValidation={showValidation}
						invalidMessage={transactionCodeInvalid}
						validations={validations}
						updateValidation={updateValidation}
						hideErrorMsg={hideErrorMsg}
						onChange={handleSelectTransactionCode}
						useValidation={!templateMode}
					/>
				</span>
			);
		};
		const renderReferenceField = (props) => {
			const {
				id,
				payment,
				content,
				handleReferenceChange,
				showValidation,
				validations,
				hideErrorMsg,
				updateValidation,
				templateMode,
			} = props;

			const { reference, isFromCreditCard } = payment;
			const { referenceLabel } = content;

			const derivedReference = isFromCreditCard ? '' : reference;
			const derivedUpdateValidation = isFromCreditCard ? () => {} : updateValidation;

			return (
				<span>
					<Label className={classnames(styles.main__inputLabel, 'ref')}>{referenceLabel}</Label>
					<Reference
						id={`field-reference--${id}`}
						maxLength={bacsReferenceMaxLength}
						reference={derivedReference}
						showValidation={showValidation}
						invalidMessage={content.referenceInvalid}
						validations={validations}
						updateValidation={derivedUpdateValidation}
						disabled={isFromCreditCard}
						hideErrorMsg={hideErrorMsg}
						handleReferenceChange={handleReferenceChange}
						useValidation={!templateMode}
						minLength={bacsReferenceMinLength}
					/>
				</span>
			);
		};

		return (
			<WrappedComponent
				{...props}
				renderReferenceField={renderReferenceField}
				renderAccountField={renderAccountField}
				renderHMRCRefOrTransactionCodeField={renderHMRCRefOrTransactionCodeField}
			/>
		);
	};

	DebitPaymentRow.propTypes = {
		id: PropTypes.string.isRequired,
		handleRemovePayment: PropTypes.func.isRequired,
		handleAmountBlur: PropTypes.func.isRequired,
		handleSourceCardClick: PropTypes.func.isRequired,
		destinationAccount: PropTypes.string.isRequired,
		payment: PropTypes.shape({
			id: PropTypes.string.isRequired,
			toAccount: PropTypes.string,
		}),
		sourcePayees: PropTypes.arrayOf(PropTypes.shape(payeePropTypes)).isRequired,
		showRemoveButton: PropTypes.bool.isRequired,
		content: PropTypes.shape({}).isRequired,
		handleReferenceChange: PropTypes.func.isRequired,
		showValidation: PropTypes.bool.isRequired,
		initValidation: PropTypes.func.isRequired,
		updateValidation: PropTypes.func.isRequired,
		validations: PropTypes.shape({}).isRequired,
		hideErrorMsg: PropTypes.func.isRequired,
		handleSelectTransactionCode: PropTypes.func.isRequired,
		sourceAccounts: PropTypes.arrayOf(PropTypes.shape(toAccountPropType)).isRequired,
		templateMode: PropTypes.bool,
	};

	return connect(mapState, mapDispatch)(DebitPaymentRow);
})(PaymentRow);
